import axios from 'axios'
import demoStore, { parseBody } from './DemoStore'
import { extractEmpresaApiPath, matchDemoRoute } from './allowlist'
import { DEMO_IDS, DEMO_IMEI, DEMO_MQTT_TOPIC_PUB, DEMO_MQTT_TOPIC_SUB } from './fixtures/mqttPacket'

let interceptorId = null

const buildOkResponse = (config, data, status = 200) => ({
    data,
    status,
    statusText: 'OK',
    headers: { 'content-type': 'application/json' },
    config,
    request: {},
})

const getQuery = (url) => {
    try {
        const fullUrl = String(url)
        const queryIndex = fullUrl.indexOf('?')
        if (queryIndex < 0) return {}
        const searchParams = new URLSearchParams(fullUrl.slice(queryIndex + 1))
        const queryParams = {}
        searchParams.forEach((value, key) => {
            queryParams[key] = value
        })
        return queryParams
    } catch (error) {
        return {}
    }
}

const resolveHandler = (handlerKey, config) => {
    const body = parseBody(config.data)
    const path = extractEmpresaApiPath(config.url) || ''
    const query = getQuery(config.url)
    const idMatch = path.match(/\/(\d+)(?:\/|$)/)
    const id = idMatch ? Number(idMatch[1]) : null

    switch (handlerKey) {
        case 'fabricantes':
            return demoStore.getLookups('fabricante')
        case 'modelos':
            return demoStore.getLookups('modelo')
        case 'operadoras':
            return demoStore.getLookups('operadora')
        case 'timezones':
            return demoStore.getLookups('timezone')
        case 'tipoAcessos':
            return demoStore.getLookups('tipo-acesso')
        case 'monitoradoTipos':
            return demoStore.getLookups('monitorado-tipo')
        case 'monitoradoTiposPaginated': {
            const tipos = demoStore.getLookups('monitorado-tipo')
            const list = Array.isArray(tipos) ? tipos : tipos?.results || []
            return { count: list.length, next: null, previous: null, results: list }
        }
        case 'permissoes':
            return demoStore.getLookups('permissoes-cliente')
        case 'modulos':
            return demoStore.getLookups('modulos')
        case 'emptyList':
            return { count: 0, next: null, previous: null, results: [] }
        case 'listEquipamentos': {
            const equipamento = demoStore.state.equipamento
            return {
                count: equipamento ? 1 : 0,
                next: null,
                previous: null,
                results: equipamento ? [equipamento] : [],
            }
        }
        case 'createEquipamento':
            return demoStore.createEquipamento({
                imei: DEMO_IMEI,
                fabricante: DEMO_IDS.fabricante,
                modelo: DEMO_IDS.modelo,
                operadora: DEMO_IDS.operadora,
                status: 'ativo',
                tipo_acesso: DEMO_IDS.tipoAcesso,
                timezone: 'America/Sao_Paulo',
                topico_personalizado: true,
                topico_publica: DEMO_MQTT_TOPIC_PUB,
                topico_recebe: DEMO_MQTT_TOPIC_SUB,
            })
        case 'getEquipamento':
            return demoStore.state.equipamento || demoStore.createEquipamento()
        case 'updateEquipamento':
            return demoStore.updateEquipamento(body)
        case 'historico':
            return demoStore.getHistorico()
        case 'listCampos':
            return demoStore.listCampos(query.equipamento || id)
        case 'createCampo': {
            const canonicalCampo = {
                equipamento: DEMO_IDS.equipamento,
                identificacao: 'temperatura',
                descricao: 'Temperatura',
                formula: '[temperatura]',
                tipo_dado_saida: 'decimal',
                tipo_grafico: 'linha',
                unidade: '°C',
                mascara: 'padrao',
                decimal: '2',
            }
            const existing = demoStore.state.campos.find(
                (campo) => campo.identificacao === 'temperatura'
            )
            if (existing) {
                Object.assign(existing, canonicalCampo)
                demoStore.persist()
                return existing
            }
            return demoStore.createCampo(canonicalCampo)
        }
        case 'updateCampo': {
            const existing = demoStore.state.campos.find((campo) => campo.id === id)
            if (!existing) return demoStore.createCampo({ ...body, id })
            Object.assign(existing, body, { id })
            demoStore.persist()
            return existing
        }
        case 'listPerfis':
            return {
                count: demoStore.state.perfil ? 1 : 0,
                next: null,
                previous: null,
                results: demoStore.state.perfil ? [demoStore.state.perfil] : [],
            }
        case 'createPerfil':
            return demoStore.createPerfil({
                nome: 'Perfil Demonstração',
                permissoes: Array.from(
                    { length: 6 },
                    (_, index) => DEMO_IDS.permissaoBase + index
                ),
            })
        case 'getPerfil':
            return demoStore.state.perfil || demoStore.createPerfil()
        case 'updatePerfil':
            return demoStore.createPerfil({ ...body, ...demoStore.state.perfil })
        case 'listClientes': {
            const cliente = demoStore.toClientePayload()
            return {
                count: cliente ? 1 : 0,
                next: null,
                previous: null,
                results: cliente ? [cliente] : [],
            }
        }
        case 'emailConfirmacao':
            return { confirmado: true }
        case 'createCliente':
            return demoStore.createCliente({
                nome_fantasia: 'Cliente Demonstração',
                razao_social: 'Cliente Demonstração LTDA',
                email_administrador: 'demo@afira.io',
                pessoa_tipo: 'fisica',
                status: 'ativo',
                perfil: DEMO_IDS.perfil,
            })
        case 'getCliente':
            return demoStore.toClientePayload() || demoStore.createCliente()
        case 'updateCliente':
            return demoStore.updateCliente(body)
        case 'listMonitoradosGrid':
            if (!demoStore.state.monitorado) {
                demoStore.seedThroughStage('monitorado-demo')
            }
            return demoStore.listMonitorados()
        case 'createMonitorado':
            return demoStore.createMonitorado({
                nome: 'Monitorado Demonstração',
                cliente_id: DEMO_IDS.cliente,
                tipo_id: DEMO_IDS.tipoMonitorado,
            })
        case 'getMonitorado':
            return demoStore.state.monitorado || demoStore.createMonitorado()
        case 'updateMonitorado':
            return demoStore.updateMonitorado(body)
        case 'listMonitoradoParametros':
            return {
                count: demoStore.state.monitoradoParametros.length,
                next: null,
                previous: null,
                results: demoStore.state.monitoradoParametros,
            }
        case 'linkParametro':
            return demoStore.linkParametro(body)
        case 'unlinkParametro':
            return demoStore.unlinkParametro(id)
        case 'listaMonitorados':
            if (!demoStore.state.monitorado) {
                demoStore.seedThroughStage('monitorado-demo')
            }
            return demoStore.listMonitorados()
        case 'dadosDashboard':
            demoStore.ensureSandboxDataset()
            return demoStore.getDadosDashboard()
        case 'monitoradoStatus':
            demoStore.ensureSandboxDataset()
            return demoStore.getMonitoradoStatus() || { id: DEMO_IDS.monitorado, parametros: [] }
        case 'statusGrafico':
            return demoStore.chart(body.campo || body.field || 'temperatura')
        case 'statusReportes':
            return demoStore.listStatusReportes()
        case 'consumo':
            return demoStore.consumo(
                body.campo ||
                    (() => {
                        try {
                            const dispositivos = JSON.parse(body.dispositivos || '[]')
                            return dispositivos?.[0]?.field
                        } catch (error) {
                            return 'volume_hidrometro'
                        }
                    })()
            )
        case 'listSupervisorios':
            demoStore.ensureSandboxDataset()
            return demoStore.listSupervisorios()
        case 'updateSupervisorio':
            return demoStore.updateSupervisorio(body)
        case 'reporteSupervisorio':
            return demoStore.reporteSupervisorio()
        case 'supervisorioHistorico':
            return demoStore.historicoSupervisorio(body)
        case 'formulaCheck': {
            const latestValues = (() => {
                try {
                    return JSON.parse(demoStore.getHistorico()?.last_report || '{}')
                } catch (error) {
                    return {}
                }
            })()
            const preferredResult =
                latestValues.temperatura ??
                latestValues.energia ??
                latestValues.nivel ??
                latestValues.ct_pulso?.[0] ??
                24.5
            return { message: true, result: String(preferredResult) }
        }
        case 'formulaTemplates':
            return {
                count: 3,
                next: null,
                previous: null,
                results: [
                    {
                        id: 900501,
                        nome: 'Cálculo de pulso',
                        formula: 'CALC_PULSE([variavel_de_pulso])',
                        descricao:
                            'Converte a contagem de pulsos em volume/consumo. Substitua a variável pelo campo de pulso do pacote.',
                    },
                    {
                        id: 900502,
                        nome: 'Média simples',
                        formula: '([variavel_a] + [variavel_b]) / 2',
                        descricao: 'Calcula a média aritmética entre dois valores do pacote.',
                    },
                    {
                        id: 900503,
                        nome: 'Diferença percentual',
                        formula: '(([atual] - [anterior]) / [anterior]) * 100',
                        descricao: 'Retorna a variação percentual entre o valor atual e o anterior.',
                    },
                ],
            }
        default:
            return null
    }
}

export const enableDemoInterceptor = () => {
    if (interceptorId !== null) return

    demoStore.load()

    interceptorId = axios.interceptors.request.use((config) => {
        const requestUrl = String(config.url || '')
        let path = extractEmpresaApiPath(config.url)

        if (path == null && /lista-monitorado/i.test(requestUrl)) {
            path = 'lista-monitorado'
        }

        if (path == null && !requestUrl.includes('formulas_check')) {
            return config
        }

        const handlerKey = matchDemoRoute(config.method, path || 'formulas_check')
        if (!handlerKey) return config

        let responseData = null
        try {
            responseData = resolveHandler(handlerKey, config)
        } catch (error) {
            console.error('Demonstração: falha ao montar resposta simulada', handlerKey, error)
            responseData = { count: 0, next: null, previous: null, results: [] }
        }

        config.adapter = () =>
            Promise.resolve(buildOkResponse(config, responseData))
        return config
    })
}

export const disableDemoInterceptor = () => {
    if (interceptorId === null) return
    axios.interceptors.request.eject(interceptorId)
    interceptorId = null
}
